import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { motion } from 'framer-motion';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { 
  Quote,
  Play,
  MapPin,
  Calendar,
  ArrowRight,
  Heart,
  TreeDeciduous,
  Users,
  Leaf,
  Mountain,
  Camera,
  Image,
  Video,
  FileText
} from 'lucide-react';

export default function CommunityPage() {
  const { t, language } = useLanguage();

  // Community voices - placeholder structure ready to be filled
  // Set to empty array until real community members are added
  const featuredVoices = [];
  const otherVoices = [];

  const events = [
    t('community.events.event1'),
    t('community.events.event2'),
    t('community.events.event3'),
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="community-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative pt-16 pb-24 overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1609137144813-7d9921338f24?w=1920&q=80"
            alt="Epuyén community"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background via-background/95 to-background" />
        </div>
        
        <div className="relative max-w-6xl mx-auto px-4 pt-16">
          <motion.div 
            className="text-center"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Users className="w-3.5 h-3.5" />
              {t('community.badge')}
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-foreground mb-6">
              {t('community.title')}
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
              {t('community.subtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Community Memory Portal Banner */}
      <section className="py-12 px-4 bg-gradient-to-r from-primary/10 via-primary/5 to-primary/10 border-y border-primary/20">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <Card className="overflow-hidden border-primary/30 bg-card/80 backdrop-blur">
              <div className="grid md:grid-cols-2 gap-0">
                {/* Left side - Image collage effect */}
                <div className="relative h-64 md:h-auto bg-gradient-to-br from-amber-500/20 via-red-500/20 to-green-500/20 overflow-hidden">
                  <div className="absolute inset-0 grid grid-cols-3 gap-1 p-4 opacity-60">
                    <div className="bg-slate-800 rounded flex items-center justify-center">
                      <Image className="w-8 h-8 text-white/50" />
                    </div>
                    <div className="bg-slate-700 rounded flex items-center justify-center">
                      <Video className="w-8 h-8 text-white/50" />
                    </div>
                    <div className="bg-slate-800 rounded flex items-center justify-center">
                      <FileText className="w-8 h-8 text-white/50" />
                    </div>
                    <div className="bg-slate-700 rounded flex items-center justify-center col-span-2">
                      <Camera className="w-10 h-10 text-white/50" />
                    </div>
                    <div className="bg-slate-800 rounded flex items-center justify-center">
                      <Heart className="w-8 h-8 text-white/50" />
                    </div>
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-r from-transparent to-card/90" />
                </div>
                
                {/* Right side - Content */}
                <div className="p-8 flex flex-col justify-center">
                  <div className="eco-badge mb-3 w-fit">
                    <Heart className="w-3.5 h-3.5" />
                    Memoria Colectiva
                  </div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
                    Community Memory Portal
                  </h2>
                  <p className="text-muted-foreground mb-6">
                    A living archive of our community&apos;s history, resilience, and hope. 
                    Share your photos, videos, and stories to help preserve our collective memory of Epuyén.
                  </p>
                  <div className="flex flex-wrap gap-3 mb-6">
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Camera className="w-4 h-4 text-primary" /> Photos
                    </span>
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <Video className="w-4 h-4 text-primary" /> Videos
                    </span>
                    <span className="flex items-center gap-1.5 text-sm text-muted-foreground">
                      <FileText className="w-4 h-4 text-primary" /> Stories
                    </span>
                  </div>
                  <Link to="/community/memoria">
                    <Button size="lg" className="btn-primary w-full sm:w-auto" data-testid="memoria-portal-btn">
                      Explore & Contribute
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                </div>
              </div>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Featured Voices */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-6xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold text-foreground mb-2">{t('community.featuredVoices.title')}</h2>
            <p className="text-muted-foreground">{t('community.featuredVoices.subtitle')}</p>
          </motion.div>

          {/* Show placeholder if no voices yet */}
          {featuredVoices.length === 0 ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <Card className="p-12 text-center border-dashed border-2 border-muted-foreground/30">
                <Users className="w-16 h-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-bold text-foreground mb-2">
                  {language === 'es' ? 'Voces de la Comunidad' : 'Community Voices'}
                </h3>
                <p className="text-muted-foreground max-w-md mx-auto mb-6">
                  {language === 'es' 
                    ? 'Pronto compartiremos las historias y testimonios de los miembros de nuestra comunidad. Si eres parte de Epuyén y quieres compartir tu voz, contáctanos.'
                    : 'We will soon share stories and testimonials from our community members. If you are part of Epuyén and want to share your voice, contact us.'}
                </p>
                <Link to="/community/memoria">
                  <Button variant="outline">
                    <Heart className="w-4 h-4 mr-2" />
                    {language === 'es' ? 'Compartir mi Historia' : 'Share My Story'}
                  </Button>
                </Link>
              </Card>
            </motion.div>
          ) : (
            <div className="grid md:grid-cols-3 gap-8">
              {featuredVoices.map((voice, index) => (
                <motion.div
                  key={voice.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Card className="natural-card overflow-hidden h-full group hover:shadow-xl transition-all">
                    <div className="relative h-64 overflow-hidden">
                      <img 
                        src={voice.image} 
                        alt={voice.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
                      <div className="absolute bottom-4 left-4 right-4">
                        <h3 className="text-xl font-bold text-white">{voice.name}</h3>
                        <p className="text-white/80 text-sm">{voice.role}</p>
                      </div>
                      <div className="absolute top-4 right-4">
                        <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center">
                          <Quote className="w-5 h-5 text-white" />
                        </div>
                      </div>
                    </div>

                    <div className="p-6">
                      <p className="text-muted-foreground italic leading-relaxed mb-4">
                        &ldquo;{voice.quote}&rdquo;
                      </p>
                      
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        {voice.location}
                      </div>
                    </div>
                  </Card>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Video Interview Section */}
      <section className="py-16 px-4 eco-gradient text-white">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="text-white/60 text-sm uppercase tracking-wider">{t('community.documentary.comingSoon')}</span>
              <h2 className="text-3xl sm:text-4xl font-bold mt-2 mb-6">
                {t('community.documentary.title')}
              </h2>
              <p className="text-white/80 leading-relaxed mb-6">
                {t('community.documentary.description')}
              </p>
              <ul className="space-y-3 mb-8">
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <Camera className="w-3 h-3" />
                  </div>
                  <span className="text-white/90">6 episodes</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <Users className="w-3 h-3" />
                  </div>
                  <span className="text-white/90">Mapudungun & Spanish</span>
                </li>
                <li className="flex items-center gap-3">
                  <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center">
                    <Mountain className="w-3 h-3" />
                  </div>
                  <span className="text-white/90">Filmed in the valley</span>
                </li>
              </ul>
              <Button className="bg-white text-primary hover:bg-white/90">
                {t('community.documentary.watchTrailer')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="aspect-video rounded-lg overflow-hidden bg-black/30 relative group cursor-pointer">
                <img 
                  src="https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=800&q=80"
                  alt="Documentary preview"
                  className="w-full h-full object-cover opacity-80 group-hover:opacity-60 transition-opacity"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-8 h-8 text-white ml-1" />
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* More Voices Grid */}
      <section className="py-16 px-4 bg-background">
        <div className="max-w-6xl mx-auto">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {otherVoices.map((voice, index) => (
              <motion.div
                key={voice.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="natural-card p-6 h-full hover:shadow-lg transition-shadow">
                  <div className="flex items-start gap-4 mb-4">
                    <img 
                      src={voice.image} 
                      alt={voice.name}
                      className="w-16 h-16 rounded-full object-cover border-2 border-primary/20"
                    />
                    <div>
                      <h3 className="font-semibold text-foreground">{voice.name}</h3>
                      <p className="text-sm text-muted-foreground">{voice.role}</p>
                      <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
                        <MapPin className="w-3 h-3" />
                        {voice.location}
                      </div>
                    </div>
                  </div>
                  <p className="text-muted-foreground text-sm italic leading-relaxed">
                    &ldquo;{voice.quote}&rdquo;
                  </p>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Community Events */}
      <section className="py-16 px-4 bg-card">
        <div className="max-w-4xl mx-auto">
          <motion.div 
            className="text-center mb-12"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <div className="eco-badge mb-4 mx-auto w-fit">
              <Calendar className="w-3.5 h-3.5" />
              {t('community.events.title')}
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-2">{t('community.events.title')}</h2>
            <p className="text-muted-foreground">{t('community.events.subtitle')}</p>
          </motion.div>

          <div className="space-y-4">
            {events.map((event, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="natural-card p-6 flex flex-col sm:flex-row sm:items-center gap-4 hover:shadow-lg transition-shadow">
                  <div className="flex-shrink-0 w-20 text-center">
                    <div className="text-2xl font-bold text-primary">{event.date?.split(' ')[0]}</div>
                    <div className="text-sm text-muted-foreground">{event.date?.split(' ').slice(1).join(' ')}</div>
                  </div>
                  <div className="flex-1 border-l border-border pl-4">
                    <h3 className="font-semibold text-foreground">{event.title}</h3>
                    <div className="flex items-center gap-1 text-sm text-muted-foreground">
                      <MapPin className="w-3 h-3" />
                      {event.location}
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    <p className="text-sm text-muted-foreground">{event.description}</p>
                  </div>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Share Your Story CTA */}
      <section className="py-16 px-4 bg-background">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center">
              <Heart className="w-8 h-8 text-primary" />
            </div>
            <h2 className="text-3xl font-bold text-foreground mb-4">{t('community.shareStory.title')}</h2>
            <p className="text-muted-foreground mb-8">
              {t('community.shareStory.description')}
            </p>
            <Link to="/community/memoria">
              <Button size="lg" className="btn-primary" data-testid="share-story-cta">
                {t('community.shareStory.cta')}
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-4 eco-gradient text-white/80">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center">
                <Leaf className="w-5 h-5" />
              </div>
              <span className="font-semibold text-lg text-white">Epuyén Renace</span>
            </div>
            <p className="text-sm opacity-70">
              © {new Date().getFullYear()} Epuyén Renace. {t('landing.footerTagline')}
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
